/* ==========================================================================
   U Brands - Cart drawer upgrades
   Free shipping block / upsell row / discount code field.
   Paired with snippets/cart-upgrades.liquid + assets/cart-upgrades.css

   Notes for future edits:
   - Dawn re-renders `.drawer__inner` on every quantity change and the whole
     `#CartDrawer` on add-to-cart, so all three blocks refresh themselves for
     free. The custom elements below re-run connectedCallback each time.
   - Nothing here depends on an app. All config lives in the Liquid snippets.
   - Upsell impressions and adds are sent to GA4 (tag GT-NN6CWB7) as
     view_item_list / add_to_cart, replacing the app's analytics tab.
   - Free shipping badge impressions are sent to GA4 as view_promotion, and
     upsell adds and applied promo codes are also written onto the cart as
     note attributes so the attribution reaches the Shopify order.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var ROUTES = window.routes || {};
  var CART_URL = ROUTES.cart_url || ROOT + 'cart';
  var CART_ADD_URL = ROUTES.cart_add_url || ROOT + 'cart/add';
  var CART_UPDATE_URL = ROUTES.cart_update_url || ROOT + 'cart/update';

  // Cart attributes written onto the order so the Shopify admin (and the
  // order export) shows what the cart drawer contributed. Underscore-
  // prefixed so they read as machine fields rather than customer copy.
  var ATTR_COUNT = '_ub_upsell_adds';
  var ATTR_ITEMS = '_ub_upsell_items';
  var ATTR_VALUE = '_ub_upsell_value';
  var ATTR_PROMO = '_ub_promo_code';

  function currencyCode() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  }

  function postConfig(body) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    };
  }

  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currencyCode(),
        minimumFractionDigits: 2,
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* ------------------------------------------------------------------------
     Analytics — GA4 via the existing gtag / dataLayer on the page.
     Fails silently if neither exists, so this is safe if the tag is removed.
     ------------------------------------------------------------------------ */

  function track(eventName, params) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: eventName }, params));
      }
    } catch (e) {
      /* analytics must never break the cart */
    }
  }

  function cardToItem(card, index, listName) {
    return {
      item_id: card.dataset.productId || '',
      item_name: card.dataset.productTitle || '',
      price: Number(card.dataset.productPrice || 0) / 100,
      quantity: 1,
      index: index,
      item_list_name: listName,
    };
  }

  // Remembers which upsell line-ups have already been reported, so a drawer
  // re-render does not double-count an impression.
  var reportedLists = {};

  // Remembers which promotions have been reported this page view, so a
  // drawer re-render does not double-count a badge impression.
  var reportedPromos = {};

  /**
   * Report a GA4 view_promotion for every free shipping badge on the page.
   * Deduped per promotion + slot, so the PDP badge and the cart drawer
   * badge each count once per page view.
   */
  function reportPromoImpressions() {
    try {
      var badges = document.querySelectorAll('[data-ub-promo-id]');
      for (var i = 0; i < badges.length; i++) {
        var el = badges[i];
        var id = el.getAttribute('data-ub-promo-id');
        var slot = el.getAttribute('data-ub-promo-slot') || 'unknown';
        var key = id + '::' + slot;
        if (reportedPromos[key]) continue;
        reportedPromos[key] = true;
        track('view_promotion', {
          promotion_id: id,
          promotion_name: (el.getAttribute('data-ub-promo-name') || '').trim(),
          creative_slot: slot,
        });
      }
    } catch (e) {
      /* analytics must never break the cart */
    }
  }

  /**
   * Record on the cart itself that an item came from the upsell row.
   * These land on the order as note attributes, so revenue attribution
   * survives checkout - which is what GA4 alone cannot give us.
   */
  function stampUpsellAttribution(cart, card) {
    try {
      var attrs = (cart && cart.attributes) || {};

      var count = parseInt(attrs[ATTR_COUNT], 10) || 0;
      var value = parseFloat(attrs[ATTR_VALUE]) || 0;

      var titles = String(attrs[ATTR_ITEMS] || '')
        .split('|')
        .map(function (t) { return t.trim(); })
        .filter(function (t) { return t !== ''; });

      var title = card.getAttribute('data-product-title') || '';
      if (title && titles.indexOf(title) === -1) titles.push(title);

      var payload = {};
      payload[ATTR_COUNT] = String(count + 1);
      payload[ATTR_ITEMS] = titles.join(' | ');
      payload[ATTR_VALUE] = (value + Number(card.getAttribute('data-product-price') || 0) / 100).toFixed(2);

      fetch(CART_UPDATE_URL + '.js', postConfig({ attributes: payload })).catch(function () {});
    } catch (e) {
      /* attribution must never break the cart */
    }
  }

  /**
   * Record which promo code was applied from the drawer field.
   */
  function stampPromoAttribution(cart, code) {
    try {
      var attrs = (cart && cart.attributes) || {};
      if (attrs[ATTR_PROMO] === code) return;
      var payload = {};
      payload[ATTR_PROMO] = code;
      fetch(CART_UPDATE_URL + '.js', postConfig({ attributes: payload })).catch(function () {});
    } catch (e) {
      /* attribution must never break the cart */
    }
  }

  /**
   * Swap the drawer contents using the freshly rendered cart-drawer section.
   */
  function applyDrawerSections(sections) {
    if (sections && sections['cart-drawer']) {
      swapDrawerHTML(sections['cart-drawer']);
    }
    if (sections && sections['cart-icon-bubble']) {
      swapCartBubble(sections['cart-icon-bubble']);
    }
  }

  function swapDrawerHTML(html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var source = parsed.querySelector('.drawer__inner');
    var target = document.querySelector('#CartDrawer .drawer__inner');
    if (source && target) {
      target.innerHTML = source.innerHTML;
      var wrapper = document.querySelector('cart-drawer');
      var isEmpty = !!parsed.querySelector('.drawer__inner-empty');
      if (wrapper) wrapper.classList.toggle('is-empty', isEmpty);
      target.classList.toggle('is-empty', isEmpty);
      reportPromoImpressions();
    }
  }

  function swapCartBubble(html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var source = parsed.querySelector('.shopify-section');
    var target = document.getElementById('cart-icon-bubble');
    if (source && target) target.innerHTML = source.innerHTML;
  }

  function refreshDrawerFromServer() {
    return fetch(CART_URL + '?section_id=cart-drawer')
      .then(function (res) {
        return res.text();
      })
      .then(function (html) {
        swapDrawerHTML(html);
      });
  }

  /**
   * Tell the rest of the theme the cart changed.
   * Source is deliberately 'cart-items': Dawn's CartItems.onCartUpdate skips
   * its own refetch for that source, and we have already swapped in fresh
   * markup, so this avoids a duplicate request and a visible flicker.
   */
  function announceCartUpdate(cartData) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cartData });
    }
  }

  /* ------------------------------------------------------------------------
     Free shipping progress bar
     Only present when ub_ship_mode = 'progress' in cart-upgrades.liquid.
     In the default 'badge' mode the drawer renders a static badge instead and
     this custom element never appears.
     ------------------------------------------------------------------------ */

  if (!customElements.get('cart-free-shipping-bar')) {
    customElements.define(
      'cart-free-shipping-bar',
      class CartFreeShippingBar extends HTMLElement {
        connectedCallback() {
          this.threshold = parseInt(this.dataset.threshold, 10) || 0;
          this.fill = this.querySelector('[data-ub-ship-fill]');
          this.msg = this.querySelector('[data-ub-ship-msg]');

          if (this.fill && !this.hasAttribute('data-ub-animated')) {
            var width = this.fill.style.width;
            this.fill.style.width = '0%';
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.fill.style.width = width;
              });
            });
            this.setAttribute('data-ub-animated', 'true');
          }

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
              if (event && event.cartData && typeof event.cartData.total_price === 'number') {
                this.update(event.cartData.total_price);
              }
            });
          }
        }

        disconnectedCallback() {
          if (this.unsubscribe) this.unsubscribe();
        }

        update(totalPrice) {
          if (!this.threshold) return;
          var remaining = this.threshold - totalPrice;
          var pct = Math.max(0, Math.min(100, Math.round((totalPrice * 100) / this.threshold)));

          if (this.fill) this.fill.style.width = pct + '%';

          var track = this.querySelector('.ub-ship__track');
          if (track) track.setAttribute('aria-valuenow', String(pct));

          this.classList.toggle('ub-ship--unlocked', remaining <= 0);

          if (this.msg) {
            if (remaining <= 0) {
              this.msg.textContent = this.dataset.winText || '';
            } else {
              this.msg.textContent = (this.dataset.awayText || '').replace('[amount]', formatMoney(remaining));
            }
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     Upsell row
     ------------------------------------------------------------------------ */

  if (!customElements.get('cart-upsell')) {
    customElements.define(
      'cart-upsell',
      class CartUpsell extends HTMLElement {
        connectedCallback() {
          this.errorEl = this.querySelector('[data-ub-upsell-error]');
          this.listName = this.dataset.listName || 'Cart drawer upsell';
          this.addEventListener('click', this.onClick.bind(this));
          this.reportImpression();
        }

        cards() {
          return [...this.querySelectorAll('.ub-upsell__card')];
        }

        reportImpression() {
          var cards = this.cards();
          if (!cards.length) return;

          var signature = cards.map(function (c) { return c.dataset.productId; }).join('|');
          if (reportedLists[signature]) return;
          reportedLists[signature] = true;

          var self = this;
          track('view_item_list', {
            item_list_name: this.listName,
            items: cards.map(function (card, i) { return cardToItem(card, i, self.listName); }),
          });
        }

        onClick(event) {
          var button = event.target.closest('[data-ub-upsell-add]');
          if (!button || button.disabled) return;
          event.preventDefault();
          this.addVariant(button);
        }

        addVariant(button) {
          var id = button.dataset.variantId;
          if (!id) return;

          var card = button.closest('.ub-upsell__card');
          var index = card ? this.cards().indexOf(card) : 0;
          var listName = this.listName;

          button.classList.add('is-loading');
          button.disabled = true;
          if (this.errorEl) this.errorEl.textContent = '';

          fetch(
            CART_ADD_URL,
            postConfig({
              items: [{ id: Number(id), quantity: 1 }],
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            })
          )
            .then(function (res) {
              return res.json().then(function (data) {
                return { ok: res.ok, data: data };
              });
            })
            .then((result) => {
              if (!result.ok) {
                button.classList.remove('is-loading');
                button.disabled = false;
                if (this.errorEl) {
                  this.errorEl.textContent =
                    result.data.description || result.data.message || 'That item could not be added.';
                }
                return;
              }

              if (card) {
                var item = cardToItem(card, index, listName);
                track('add_to_cart', {
                  currency: currencyCode(),
                  value: item.price,
                  items: [item],
                });
              }

              applyDrawerSections(result.data.sections);

              // Pull fresh totals so any threshold UI reflects the new subtotal.
              return fetch(CART_URL + '.js')
                .then(function (res) {
                  return res.json();
                })
                .then(function (cart) {
                  announceCartUpdate(cart);
                  if (card) stampUpsellAttribution(cart, card);
                });
            })
            .catch(() => {
              button.classList.remove('is-loading');
              button.disabled = false;
              if (this.errorEl) this.errorEl.textContent = 'Something went wrong. Please try again.';
            });
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     Discount code field

     Shopify has no public "apply discount to cart" AJAX endpoint. The
     supported trick is to hit /discount/<CODE> with a redirect back to
     /cart.js: Shopify sets the discount cookie and hands us the updated cart
     as JSON. Order-level and product discounts show up right away; free
     shipping and some Shopify Functions discounts only resolve at checkout,
     which is why the failure copy says so instead of claiming the code is bad.
     ------------------------------------------------------------------------ */

  if (!customElements.get('cart-discount-form')) {
    customElements.define(
      'cart-discount-form',
      class CartDiscountForm extends HTMLElement {
        connectedCallback() {
          this.toggle = this.querySelector('[data-ub-disc-toggle]');
          this.body = this.querySelector('.ub-disc__body');
          this.input = this.querySelector('[data-ub-disc-input]');
          this.applyBtn = this.querySelector('[data-ub-disc-apply]');
          this.msg = this.querySelector('[data-ub-disc-msg]');

          if (this.toggle) this.toggle.addEventListener('click', this.onToggle.bind(this));
          if (this.applyBtn) this.applyBtn.addEventListener('click', this.onApply.bind(this));
          if (this.input) {
            this.input.addEventListener('keydown', (event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                this.onApply();
              }
            });
          }
        }

        onToggle() {
          var expanded = this.toggle.getAttribute('aria-expanded') === 'true';
          this.toggle.setAttribute('aria-expanded', String(!expanded));
          if (this.body) this.body.hidden = expanded;
          if (!expanded && this.input) this.input.focus();
        }

        setMessage(text, type) {
          if (!this.msg) return;
          this.msg.textContent = text;
          this.msg.classList.remove('ub-disc__msg--error', 'ub-disc__msg--success');
          if (type) this.msg.classList.add('ub-disc__msg--' + type);
        }

        onApply() {
          var code = (this.input && this.input.value ? this.input.value : '').trim();
          if (!code) {
            this.setMessage('Enter a code first.', 'error');
            return;
          }

          this.applyBtn.classList.add('is-loading');
          this.applyBtn.disabled = true;
          this.setMessage('', null);

          fetch(ROOT + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(ROOT + 'cart.js'), {
            headers: { Accept: 'application/json' },
          })
            .then(function (res) {
              return res.json();
            })
            .then((cart) => {
              var cartLevel = (cart && cart.cart_level_discount_applications) || [];
              var lineLevel = ((cart && cart.items) || []).some(function (item) {
                return item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0;
              });
              var codes = ((cart && cart.discount_codes) || []).filter(function (d) {
                return d.applicable !== false;
              });

              var applied = cartLevel.length > 0 || lineLevel || codes.length > 0;

              if (applied) {
                track('select_promotion', { promotion_name: code.toUpperCase() });
                stampPromoAttribution(cart, code.toUpperCase());
              }

              var message = applied
                ? { text: code.toUpperCase() + ' applied.', type: 'success' }
                : {
                    text:
                      "We couldn't apply that code to this cart. Free shipping and some automatic codes only show at checkout.",
                    type: 'error',
                  };

              return refreshDrawerFromServer().then(function () {
                // The drawer re-rendered, so write the message into the new node.
                var freshForm = document.querySelector('cart-discount-form');
                if (!freshForm) return;
                var freshToggle = freshForm.querySelector('[data-ub-disc-toggle]');
                var freshBody = freshForm.querySelector('.ub-disc__body');
                if (freshToggle) freshToggle.setAttribute('aria-expanded', 'true');
                if (freshBody) freshBody.hidden = false;
                if (typeof freshForm.setMessage === 'function') {
                  freshForm.setMessage(message.text, message.type);
                }
                announceCartUpdate(cart);
              });
            })
            .catch(() => {
              this.applyBtn.classList.remove('is-loading');
              this.applyBtn.disabled = false;
              this.setMessage('Something went wrong. Please try again.', 'error');
            });
        }
      }
    );
  }

  /* ------------------------------------------------------------------------
     Initial promotion impressions for badges already in the page (the PDP
     badge, and the drawer badge when the cart is not empty on load).
     ------------------------------------------------------------------------ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reportPromoImpressions);
  } else {
    reportPromoImpressions();
  }
})();